// AdminPricing.jsx — per-store special pricing, presentation-sheet style.
// 2026-08-24 (Brian): store picker is MULTI-select — pick sister stores
// (two Kim's Marts...) as chips and edit them together: type a price once,
// one Save + one confirm writes it to every selected store. One store
// selected = classic single-store editing, prefilled from its list.
// With several stores, fields prefill only where ALL stores already match;
// otherwise they start blank and the row shows each store's current value,
// so bulk-saving never overwrites a differing price invisibly.
// Every change still sits behind an are-you-sure popup. Admin-only.
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getAllProducts, getCustomerRecords } from "../services/dataCache";
import {
  getCustomerPricing, saveSpecialPrice, removeSpecialPrice, isSpecialActive
} from "../services/customerPricingService";
import { productMatches } from "../utils/searchMatch";

function skuCompare(a, b) {
  const numA = parseInt(a.sku, 10);
  const numB = parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

function endsAtMillis(entry) {
  if (!entry?.endsAt) return null;
  if (typeof entry.endsAt.toMillis === "function") return entry.endsAt.toMillis();
  const t = new Date(entry.endsAt).getTime();
  return Number.isNaN(t) ? null : t;
}

function endsAtInputValue(entry) {
  const ms = endsAtMillis(entry);
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function entriesEqual(a, b) {
  if (!a || !b) return false;
  return a.price === b.price
    && (a.active !== false) === (b.active !== false)
    && endsAtMillis(a) === endsAtMillis(b);
}

function entryToEdit(entry) {
  return {
    price: entry?.price != null ? String(entry.price) : "",
    endsAt: endsAtInputValue(entry),
    active: entry?.active !== false
  };
}

const EMPTY_EDIT = { price: "", endsAt: "", active: true };

export default function AdminPricing() {
  const { user } = useAuth();
  const [params] = useSearchParams(); // ?record=<id> preselects (from Customers page)

  // Overview (Brian, 2026-08-25): before any store is picked, every store
  // that HAS special prices is listed — click the name and their list drops
  // down (like the Orders page rows), with an Edit jump.
  const [overview, setOverview] = useState({}); // recordId -> pricing doc data
  const [expandedStore, setExpandedStore] = useState(null);

  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stores, setStores] = useState([]);          // selected records (1..n)
  const [savedByStore, setSavedByStore] = useState({}); // recordId -> items map
  const [custSearch, setCustSearch] = useState("");
  const [edits, setEdits] = useState({});            // sku -> {price, endsAt, active}
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [view, setView] = useState("all");           // all | special
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    async function load() {
      const [recs, prods] = await Promise.all([getCustomerRecords(), getAllProducts()]);
      const sortedRecs = [...recs].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRecords(sortedRecs);
      setProducts([...prods].sort(skuCompare));
      try {
        const snap = await getDocs(collection(db, "customerPricing"));
        setOverview(Object.fromEntries(snap.docs.map(d => [d.id, d.data()])));
      } catch (err) {
        console.error("Could not load pricing overview:", err.message);
      }
      setLoading(false);
      const preselect = params.get("record");
      if (preselect) {
        const r = sortedRecs.find(x => x.id === preselect);
        if (r) addStore(r, []);
      }
    }
    load();
    // params/addStore intentionally not deps — preselect runs once at load
    // (the exhaustive-deps warning here is expected and harmless).
  }, []);

  // Prefill rule: with ONE store, edits mirror its list. With several, a SKU
  // prefills only when every selected store has the identical entry —
  // otherwise blank, and the row's per-store hint shows the differences.
  // WHAT'S ON SCREEN IS THE WORKING SET (Brian, 2026-08-25): adding or
  // removing a store NEVER resets values already showing — typed-but-unsaved
  // prices survive, and Save all then writes the visible set to every
  // selected store. Only rows the user hasn't touched get (re)prefilled.
  function rebuildEdits(selected, savedMap) {
    setEdits(prev => {
      const skus = new Set();
      for (const r of selected) {
        for (const sku of Object.keys(savedMap[r.id] || {})) skus.add(sku);
      }
      const next = { ...prev }; // keep everything already on screen
      for (const sku of skus) {
        if (next[sku]) continue; // user's row — never clobber
        const entries = selected.map(r => (savedMap[r.id] || {})[sku]);
        const first = entries.find(Boolean);
        const allSame = entries.every(e => entriesEqual(e, first));
        next[sku] = (first && allSame && entries.length === selected.length)
          ? entryToEdit(first)
          : (selected.length === 1 ? entryToEdit(first) : { ...EMPTY_EDIT });
      }
      return next;
    });
  }

  async function addStore(r, current = stores) {
    if (current.some(x => x.id === r.id)) return;
    const data = await getCustomerPricing(r.id, true); // fresh, never cached
    const items = data.items || {};
    const selected = [...current, r];
    const savedMap = { ...savedByStore, [r.id]: items };
    setStores(selected);
    setSavedByStore(savedMap);
    setCustSearch("");
    rebuildEdits(selected, savedMap);
  }

  function dropStore(id) {
    const selected = stores.filter(r => r.id !== id);
    setStores(selected);
    rebuildEdits(selected, savedByStore);
    // Back at the overview? Fold this session's saves into it so it's fresh.
    if (selected.length === 0) {
      setOverview(prev => {
        const next = { ...prev };
        for (const [rid, items] of Object.entries(savedByStore)) {
          next[rid] = { ...(next[rid] || {}), items };
        }
        return next;
      });
    }
  }

  function popFlash(key) {
    setFlash(key);
    setTimeout(() => setFlash(null), 2000);
  }

  const editFor = (sku) => edits[sku] || EMPTY_EDIT;
  const patchEdit = (sku, patch) =>
    setEdits(prev => ({ ...prev, [sku]: { ...EMPTY_EDIT, ...prev[sku], ...patch } }));

  const savedEntries = (sku) => stores
    .map(r => ({ store: r, entry: (savedByStore[r.id] || {})[sku] }))
    .filter(x => x.entry);

  async function saveItem(p) {
    const edit = editFor(p.sku);
    const price = parseFloat(edit.price);
    if (!(price > 0)) { alert("Enter a special price above $0 first."); return; }
    if (price >= p.basePrice && !window.confirm(
      `⚠ The special price ($${price.toFixed(2)}) is NOT below the regular price ($${p.basePrice.toFixed(2)}).\n\nSave it anyway?`)) return;
    const untilText = edit.endsAt ? `\nEffective until: ${edit.endsAt}` : "\nNo end date (until you change it)";
    const storeNames = stores.map(r => r.name).join("\n  - ");
    if (!window.confirm(
      `Set special price at ${stores.length} store${stores.length !== 1 ? "s" : ""}?\n  - ${storeNames}\n\n${p.name}\nRegular $${p.basePrice.toFixed(2)} → Special $${price.toFixed(2)}${untilText}\n\nEVERY order for ${stores.length !== 1 ? "these stores" : "this store"} will use this price. Continue?`)) return;
    setBusy(true);
    try {
      const entry = {
        price,
        endsAt: edit.endsAt ? new Date(`${edit.endsAt}T23:59:59`) : null,
        active: edit.active !== false
      };
      const savedMap = { ...savedByStore };
      for (const r of stores) {
        await saveSpecialPrice(r.id, p.sku, entry, user.uid);
        savedMap[r.id] = { ...(savedMap[r.id] || {}), [p.sku]: entry };
      }
      setSavedByStore(savedMap);
      popFlash(p.sku);
    } catch (err) {
      alert(`Could not save: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  // Single store: clicking Active on a SAVED entry writes immediately (with
  // confirm) — the one-click pause/resume. Several stores: the checkbox is
  // just part of what Save will apply (per-store toggling = single mode).
  async function toggleActive(p) {
    const single = stores.length === 1 ? stores[0] : null;
    const entry = single ? (savedByStore[single.id] || {})[p.sku] : null;
    if (!single || !entry) {
      patchEdit(p.sku, { active: !editFor(p.sku).active });
      return;
    }
    const turningOn = entry.active === false;
    if (!window.confirm(turningOn
      ? `Turn special price back ON for ${p.name} at ${single.name}?`
      : `Turn special price OFF for ${p.name} at ${single.name}?\n\nThe store goes back to regular/promo pricing immediately. The price is kept so you can turn it back on later.`)) return;
    setBusy(true);
    try {
      const next = { ...entry, active: turningOn };
      await saveSpecialPrice(single.id, p.sku, next, user.uid);
      setSavedByStore(prev => ({
        ...prev, [single.id]: { ...(prev[single.id] || {}), [p.sku]: next }
      }));
      patchEdit(p.sku, { active: turningOn });
      popFlash(p.sku);
    } catch (err) {
      alert(`Could not save: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(p) {
    const holders = savedEntries(p.sku);
    if (holders.length === 0) { // never saved anywhere — just clear the inputs
      setEdits(prev => { const n = { ...prev }; delete n[p.sku]; return n; });
      return;
    }
    const names = holders.map(h => h.store.name).join(", ");
    if (!window.confirm(`Remove the special price for ${p.name} at: ${names}?\n\n(To pause instead, use the Active checkbox.)`)) return;
    setBusy(true);
    try {
      const savedMap = { ...savedByStore };
      for (const h of holders) {
        await removeSpecialPrice(h.store.id, p.sku, user.uid);
        const items = { ...(savedMap[h.store.id] || {}) };
        delete items[p.sku];
        savedMap[h.store.id] = items;
      }
      setSavedByStore(savedMap);
      setEdits(prev => { const n = { ...prev }; delete n[p.sku]; return n; });
    } catch (err) {
      alert(`Could not remove: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  // "Save all" (Brian, 2026-08-25): every row whose typed values differ from
  // what's saved (at any selected store) — ONE confirm, ONE pass.
  function editDiffers(sku) {
    const edit = edits[sku];
    if (!edit) return false;
    const price = parseFloat(edit.price);
    if (!(price > 0)) return false;
    return stores.some(r => {
      const saved = (savedByStore[r.id] || {})[sku];
      if (!saved) return true;
      return saved.price !== price
        || endsAtInputValue(saved) !== (edit.endsAt || "")
        || (saved.active !== false) !== (edit.active !== false);
    });
  }
  const pendingSkus = Object.keys(edits).filter(editDiffers);

  async function saveAll() {
    const items = pendingSkus
      .map(sku => ({ sku, p: products.find(x => x.sku === sku), edit: edits[sku] }))
      .filter(x => x.p);
    if (items.length === 0) return;
    const overBase = items.filter(x => parseFloat(x.edit.price) >= x.p.basePrice);
    const lines = items.slice(0, 12).map(x =>
      `  - ${x.p.name}: $${parseFloat(x.edit.price).toFixed(2)}${x.edit.endsAt ? ` (until ${x.edit.endsAt})` : ""}`
    ).join("\n");
    if (!window.confirm(
      `Save ${items.length} special price${items.length !== 1 ? "s" : ""} at ${stores.map(r => r.name).join(", ")}?\n\n${lines}` +
      `${items.length > 12 ? `\n  …and ${items.length - 12} more` : ""}` +
      `${overBase.length ? `\n\n⚠ ${overBase.length} price(s) are NOT below regular — they save as typed.` : ""}` +
      `\n\nEVERY order for ${stores.length !== 1 ? "these stores" : "this store"} will use these prices. Continue?`)) return;
    setBusy(true);
    try {
      const savedMap = { ...savedByStore };
      for (const x of items) {
        const entry = {
          price: parseFloat(x.edit.price),
          endsAt: x.edit.endsAt ? new Date(`${x.edit.endsAt}T23:59:59`) : null,
          active: x.edit.active !== false
        };
        for (const r of stores) {
          await saveSpecialPrice(r.id, x.sku, entry, user.uid);
          savedMap[r.id] = { ...(savedMap[r.id] || {}), [x.sku]: entry };
        }
      }
      setSavedByStore(savedMap);
      popFlash("__all__");
    } catch (err) {
      alert(`Could not save: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  const specialSkuCount = new Set(
    stores.flatMap(r => Object.keys(savedByStore[r.id] || {}))
  ).size;

  const filteredRecords = records.filter(r => {
    if (stores.some(x => x.id === r.id)) return false;
    const s = custSearch.toLowerCase();
    return (r.name || "").toLowerCase().includes(s)
      || (r.qbCustomerName || "").toLowerCase().includes(s)
      || (r.phone || "").includes(custSearch);
  });

  const filtered = products.filter(p => {
    if (view === "special" && savedEntries(p.sku).length === 0 && !edits[p.sku]) return false;
    const matchesSearch = productMatches(p, search); // punctuation-blind
    const matchesBrand = brandFilter === "all" || p.brand === brandFilter;
    return matchesSearch && matchesBrand;
  });

  if (loading) return <p>Loading...</p>;

  return (
    <div className="presentation-page">
      <div className="presentation-builder">
        <h2>💲 Special Pricing</h2>

        <div className="order-for-panel">
          <p className="order-for-label">
            Store{stores.length > 1 ? `s — editing ${stores.length} together` : ""}
          </p>
          {stores.length > 0 && (
            <div className="pricing-store-chips">
              {stores.map(r => (
                <span key={r.id} className="pricing-store-chip">
                  {r.name}
                  <button className="pricing-chip-x" title="Remove from selection" onClick={() => dropStore(r.id)}>×</button>
                </span>
              ))}
            </div>
          )}
          <input
            className="order-for-search"
            placeholder={stores.length === 0
              ? "Search stores by name, QB name, or phone..."
              : "Add another store to edit together..."}
            value={custSearch}
            onChange={(e) => setCustSearch(e.target.value)}
          />
          <select
            className="order-for-dropdown"
            value=""
            onChange={(e) => {
              const r = records.find(x => x.id === e.target.value);
              if (r) addStore(r);
            }}
          >
            <option value="">
              {stores.length === 0 ? `Select a store (${filteredRecords.length})` : `Add a store (${filteredRecords.length})`}
            </option>
            {filteredRecords.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}{r.phone ? ` · ${r.phone}` : ""}
              </option>
            ))}
          </select>
        </div>

        {stores.length > 0 && (
          <>
            <p className="inventory-help">
              {stores.length > 1
                ? "Editing several stores at once: Save writes the SAME price to every selected store (one confirm). Fields prefill only where all stores already match — otherwise the row shows each store's current price so nothing gets overwritten invisibly. For one-store tweaks, remove the other chips."
                : "Set a price and hit Save — every change asks you to confirm. The store's portal shows regular price crossed out + special price in red; QuickBooks invoice lines read \"special promotion\". Active unticked = paused (price kept). A date auto-ends the price that night. Store price wins over public promos."}
            </p>

            <div className="presentation-controls">
              <input
                placeholder="Search by name, brand, or SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
                <option value="all">All Brands</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <button
                className={`view-chip${view === "special" ? " active" : ""}`}
                onClick={() => setView(view === "special" ? "all" : "special")}
              >
                💲 Special only ({specialSkuCount})
              </button>
              <button
                className="mini-btn save-all-btn"
                disabled={busy || pendingSkus.length === 0}
                onClick={saveAll}
              >
                {flash === "__all__" ? "Saved ✓" : `💾 Save all (${pendingSkus.length})`}
              </button>
            </div>

            <div className="presentation-pick-list pricing-list">
              {filtered.map(p => {
                const holders = savedEntries(p.sku);
                const edit = editFor(p.sku);
                return (
                  <div key={p.sku} className={`pick-row${holders.length > 0 ? " pricing-row-set" : ""}`}>
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt="" className="pick-thumb" loading="lazy" />
                      : <span className="pick-thumb pick-thumb-empty" />}
                    <span className="pick-sku">{p.sku}</span>
                    <span className="pick-name">
                      {p.name}{!p.active ? " (inactive)" : ""}
                      {holders.length > 0 && (
                        <span className="order-for-hint">
                          {" "}{holders.slice(0, 4).map(h =>
                            `${stores.length > 1 ? `${h.store.name} ` : ""}$${h.entry.price.toFixed(2)} ${isSpecialActive(h.entry) ? "✅" : "⏸"}`
                          ).join(" · ")}
                          {holders.length > 4 ? ` · +${holders.length - 4} more` : ""}
                        </span>
                      )}
                    </span>
                    <span className="pick-promo">
                      $<input
                        type="number" min="0.01" step="0.01"
                        className="pricing-price-input"
                        placeholder="special"
                        value={edit.price}
                        onChange={(e) => patchEdit(p.sku, { price: e.target.value })}
                      />
                      <input
                        type="date"
                        value={edit.endsAt}
                        title="Blank = no end date"
                        onChange={(e) => patchEdit(p.sku, { endsAt: e.target.value })}
                      />
                      <label className="pricing-active-label" title="Unticked = paused; the price is kept">
                        <input
                          type="checkbox"
                          checked={edit.active !== false}
                          disabled={busy}
                          onChange={() => toggleActive(p)}
                        /> Active
                      </label>
                      <button className="mini-btn" disabled={busy} onClick={() => saveItem(p)}>
                        {flash === p.sku ? "Saved ✓" : stores.length > 1 ? `Save ×${stores.length}` : "Save"}
                      </button>
                      {(holders.length > 0 || edits[p.sku]) && (
                        <button className="mini-btn danger" disabled={busy} onClick={() => removeItem(p)}>
                          ✕
                        </button>
                      )}
                    </span>
                    <span className="pick-price">${p.basePrice.toFixed(2)}</span>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p className="pick-empty">
                  {view === "special" ? "No special prices here yet — switch off the filter and set some." : "No products match your search."}
                </p>
              )}
            </div>
          </>
        )}
        {stores.length === 0 && (() => {
          const productBySku = Object.fromEntries(products.map(p => [p.sku, p]));
          const withPricing = records.filter(r => Object.keys(overview[r.id]?.items || {}).length > 0);
          return (
            <div className="pricing-overview">
              <h3>Stores with special pricing ({withPricing.length})</h3>
              {withPricing.length === 0 ? (
                <p className="pick-empty">No special prices anywhere yet — pick a store above to set some.</p>
              ) : (
                <table>
                  <thead>
                    <tr><th>Store</th><th>Items</th><th>In force</th><th></th></tr>
                  </thead>
                  <tbody>
                    {withPricing.map(r => {
                      const entries = Object.entries(overview[r.id].items)
                        .sort(([a], [b]) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
                      const live = entries.filter(([, e]) => isSpecialActive(e)).length;
                      return (
                        <Fragment key={r.id}>
                          <tr
                            className="clickable order-row"
                            onClick={() => setExpandedStore(expandedStore === r.id ? null : r.id)}
                          >
                            <td>{r.name}</td>
                            <td>{entries.length}</td>
                            <td>{live > 0 ? `✅ ${live}` : "⏸ none"}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button className="mini-btn" onClick={() => addStore(r)}>Edit</button>
                            </td>
                          </tr>
                          {expandedStore === r.id && (
                            <tr>
                              <td colSpan={4}>
                                <table className="order-items-table">
                                  <thead>
                                    <tr><th>Product</th><th>Regular</th><th>Special</th><th>Until</th><th>Status</th></tr>
                                  </thead>
                                  <tbody>
                                    {entries.map(([sku, e]) => {
                                      const p = productBySku[sku];
                                      return (
                                        <tr key={sku}>
                                          <td>{p ? p.name : sku} <span className="item-sku-inline">#{sku}</span></td>
                                          <td>{p ? `$${p.basePrice.toFixed(2)}` : "—"}</td>
                                          <td className="promo-price">${(e.price || 0).toFixed(2)}</td>
                                          <td>{endsAtInputValue(e) || "—"}</td>
                                          <td>{isSpecialActive(e) ? "✅ in force" : "⏸ off"}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
